import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Triangle, RotateCcw, Star, Target, Info, Zap, Move, Maximize2, Plus, Trash2, MousePointer
} from 'lucide-react';

const CANVAS_W = 700;
const CANVAS_H = 500;
const GRID_SPACING = 25;
const SNAP = true;

const MISSIONS = [
  { id: 1, name: 'Triangle', description: 'Create a triangle with 3 points', requiredPoints: 3, stars: 1, type: 'triangle' },
  { id: 2, name: 'Rectangle', description: 'Create a rectangle with 4 points', requiredPoints: 4, stars: 2, type: 'rectangle' },
  { id: 3, name: 'Pentagon', description: 'Create a pentagon with 5 points', requiredPoints: 5, stars: 2, type: 'polygon' },
  { id: 4, name: 'Free Draw', description: 'Create any shape and explore its properties', requiredPoints: 0, stars: 1, type: 'free' },
  { id: 5, name: 'Hexagon', description: 'Create a regular hexagon with 6 points', requiredPoints: 6, stars: 3, type: 'polygon' },
  { id: 6, name: 'Parallelogram', description: 'Create a parallelogram with 4 points (opposite sides parallel)', requiredPoints: 4, stars: 2, type: 'parallelogram' },
  { id: 7, name: 'Rhombus', description: 'Create a rhombus with 4 equal sides', requiredPoints: 4, stars: 2, type: 'rhombus' },
  { id: 8, name: 'Star Shape', description: 'Create a 5-pointed star shape with 10 points', requiredPoints: 10, stars: 3, type: 'polygon' },
  { id: 9, name: 'Octagon', description: 'Create a regular octagon with 8 points', requiredPoints: 8, stars: 3, type: 'polygon' },
  { id: 10, name: 'Quadrilateral Challenge', description: 'Create a quadrilateral and calculate its diagonals', requiredPoints: 4, stars: 2, type: 'polygon' },
];

function snapToGrid(v) {
  return SNAP ? Math.round(v / GRID_SPACING) * GRID_SPACING : v;
}

function dist(a, b) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function angleBetween(a, b, c) {
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const cross = ab.x * cb.y - ab.y * cb.x;
  let angle = Math.atan2(cross, dot) * (180 / Math.PI);
  if (angle < 0) angle += 360;
  return angle;
}

function polygonArea(pts) {
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += pts[i].x * pts[j].y;
    area -= pts[j].x * pts[i].y;
  }
  return Math.abs(area) / 2;
}

function perimeter(pts) {
  let p = 0;
  for (let i = 0; i < pts.length; i++) {
    p += dist(pts[i], pts[(i + 1) % pts.length]);
  }
  return p;
}

export default function GeometryLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('geometry');
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [dragging, setDragging] = useState(-1);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('geometry', 10);
  const [hoveredPoint, setHoveredPoint] = useState(-1);
  const [tool, setTool] = useState('add'); // 'add' | 'move'

  const mission = MISSIONS[currentMission];

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= CANVAS_W; x += GRID_SPACING) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, CANVAS_H); ctx.stroke();
    }
    for (let y = 0; y <= CANVAS_H; y += GRID_SPACING) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(CANVAS_W, y); ctx.stroke();
    }

    // Axes
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(CANVAS_W / 2, 0); ctx.lineTo(CANVAS_W / 2, CANVAS_H); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, CANVAS_H / 2); ctx.lineTo(CANVAS_W, CANVAS_H / 2); ctx.stroke();

    // Axis labels
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.font = '10px monospace';
    ctx.fillText('X', CANVAS_W - 15, CANVAS_H / 2 - 5);
    ctx.fillText('Y', CANVAS_W / 2 + 5, 12);

    if (points.length === 0) return;

    // Fill shape
    if (points.length >= 3) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(96, 165, 250, 0.08)';
      ctx.fill();
    }

    // Edges
    if (points.length >= 2) {
      for (let i = 0; i < points.length; i++) {
        const j = (i + 1) % points.length;
        const a = points[i];
        const b = points[j];

        // Edge line
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = 'rgba(96, 165, 250, 0.6)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Length label
        const d = dist(a, b) / GRID_SPACING;
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        ctx.fillStyle = '#94a3b8';
        ctx.font = '11px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(d.toFixed(1), mx, my - 8);
      }
    }

    // Angle arcs
    if (points.length >= 3) {
      for (let i = 0; i < points.length; i++) {
        const prev = points[(i - 1 + points.length) % points.length];
        const curr = points[i];
        const next = points[(i + 1) % points.length];
        const angle = angleBetween(prev, curr, next);

        // Small arc
        const startAngle = Math.atan2(prev.y - curr.y, prev.x - curr.x);
        const endAngle = Math.atan2(next.y - curr.y, next.x - curr.x);
        ctx.beginPath();
        ctx.arc(curr.x, curr.y, 18, startAngle, endAngle, angle > 180);
        ctx.strokeStyle = 'rgba(250, 204, 21, 0.5)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Angle label
        const midAngle = (startAngle + endAngle) / 2;
        const labelR = 28;
        ctx.fillStyle = '#facc15';
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(
          `${angle.toFixed(0)}°`,
          curr.x + Math.cos(midAngle) * labelR,
          curr.y + Math.sin(midAngle) * labelR
        );
      }
    }

    // Points
    points.forEach((p, i) => {
      const isHovered = hoveredPoint === i;
      const isDragged = dragging === i;

      // Glow
      if (isHovered || isDragged) {
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 20);
        glow.addColorStop(0, 'rgba(96,165,250,0.3)');
        glow.addColorStop(1, 'rgba(96,165,250,0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 20, 0, Math.PI * 2);
        ctx.fill();
      }

      // Point
      ctx.beginPath();
      ctx.arc(p.x, p.y, isHovered || isDragged ? 7 : 5, 0, Math.PI * 2);
      ctx.fillStyle = isDragged ? '#facc15' : '#60a5fa';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Label
      ctx.fillStyle = '#e2e8f0';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(String.fromCharCode(65 + i), p.x, p.y - 12);

      // Coordinates
      const gridX = ((p.x - CANVAS_W / 2) / GRID_SPACING).toFixed(0);
      const gridY = (-(p.y - CANVAS_H / 2) / GRID_SPACING).toFixed(0);
      ctx.fillStyle = 'rgba(148,163,184,0.5)';
      ctx.font = '9px monospace';
      ctx.fillText(`(${gridX},${gridY})`, p.x, p.y + 18);
    });
  }, [points, hoveredPoint, dragging]);

  useEffect(() => { draw(); }, [draw]);

  const handleCanvasClick = (e) => {
    if (tool !== 'add') return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const x = snapToGrid((e.clientX - rect.left) * scaleX);
    const y = snapToGrid((e.clientY - rect.top) * scaleY);
    setPoints(prev => [...prev, { x, y }]);
  };

  const handleMouseDown = (e) => {
    if (tool !== 'move') return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;
    const idx = points.findIndex(p => dist(p, { x: mx, y: my }) < 15);
    if (idx >= 0) setDragging(idx);
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;

    if (dragging >= 0) {
      setPoints(prev => prev.map((p, i) => i === dragging ? { x: snapToGrid(mx), y: snapToGrid(my) } : p));
    } else {
      const idx = points.findIndex(p => dist(p, { x: mx, y: my }) < 15);
      setHoveredPoint(idx);
    }
  };

  const handleMouseUp = () => { setDragging(-1); };

  const clearPoints = () => { setPoints([]); };

  const checkShape = () => {
    if (mission.type === 'free') {
      if (points.length >= 3) {
        if (!completedMissions.includes(currentMission)) {
          saveProgress(currentMission);
        }
      }
      return;
    }
    if (points.length === mission.requiredPoints) {
      if (!completedMissions.includes(currentMission)) {
        saveProgress(currentMission);
      }
    }
  };

  // Stats
  const area = points.length >= 3 ? (polygonArea(points) / (GRID_SPACING * GRID_SPACING)).toFixed(2) : '0';
  const perim = points.length >= 2 ? (perimeter(points) / GRID_SPACING).toFixed(2) : '0';

  return (
    <div className="min-h-screen bg-background" data-testid="geometry-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Mathematics lab about geometry. Students drag points to create shapes, measure angles, calculate areas, and explore transformations."
        cacheKey="lab-intro-geometry"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Triangle className="w-5 h-5 text-teal-400" />
              <span className="font-semibold">Geometry Playground</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MISSIONS.map((m, i) => (
              <Badge
                key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => { clearPoints(); setCurrentMission(i); }}
                data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) ? <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" /> : null}
                {m.name}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Mission Info */}
        <motion.div
          key={currentMission}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-teal-500/10 border border-teal-500/20"
        >
          <Target className="w-5 h-5 text-teal-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span>
          </div>
          <div className="ml-auto flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Canvas */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden border border-border/50 bg-card">
              <canvas
                ref={canvasRef}
                width={CANVAS_W}
                height={CANVAS_H}
                className="w-full cursor-crosshair"
                onClick={handleCanvasClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                data-testid="geometry-canvas"
              />
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex gap-1 p-1 rounded-lg bg-muted/50">
                <Button
                  variant={tool === 'add' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setTool('add')}
                  data-testid="tool-add"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Point
                </Button>
                <Button
                  variant={tool === 'move' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setTool('move')}
                  data-testid="tool-move"
                >
                  <Move className="w-4 h-4 mr-1" /> Move
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={clearPoints} data-testid="clear-points-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Clear
              </Button>
              <Button size="sm" onClick={checkShape} disabled={points.length < 3} data-testid="check-shape-btn">
                <Star className="w-4 h-4 mr-1" /> Check
              </Button>
            </div>
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            {/* Tools info */}
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <MousePointer className="w-4 h-4" /> Tool: {tool === 'add' ? 'Add Points' : 'Move Points'}
              </h4>
              <p className="text-xs text-muted-foreground">
                {tool === 'add' 
                  ? 'Click on the grid to place points. Points snap to the grid.' 
                  : 'Click and drag points to move them.'}
              </p>
            </div>

            {/* Shape Properties */}
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Maximize2 className="w-4 h-4 text-teal-400" /> Properties
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Points</span>
                  <span className="font-mono">{points.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sides</span>
                  <span className="font-mono">{points.length >= 2 ? points.length : 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Perimeter</span>
                  <span className="font-mono">{perim} units</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Area</span>
                  <span className="font-mono">{area} sq units</span>
                </div>
              </div>
            </div>

            {/* Points list */}
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <h4 className="text-sm font-semibold mb-3">Points</h4>
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {points.map((p, i) => {
                  const gridX = ((p.x - CANVAS_W / 2) / GRID_SPACING).toFixed(0);
                  const gridY = (-(p.y - CANVAS_H / 2) / GRID_SPACING).toFixed(0);
                  return (
                    <div key={i} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-muted/30">
      <span className="font-semibold">{String.fromCharCode(65 + i)}</span>
                      <span className="font-mono text-muted-foreground">({gridX}, {gridY})</span>
                      <button
                        onClick={() => setPoints(prev => prev.filter((_, j) => j !== i))}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                {points.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-2">No points yet</p>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
              <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
                <Info className="w-3 h-3" /> Geometry
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Place points on the coordinate grid to create shapes. The tool automatically calculates
                edge lengths, interior angles, perimeter, and area. Drag points to see how changing
                vertices affects the shape's properties in real time!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
